
import EDTableCell from '../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTableRow from '../../../../../../BaseComponents/Table/Components/EDTableRow';
import { tail } from 'ramda';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { AvenyCollectionMap } from '../../../../../../Model/ModelContext/ModelContext';
import {
  getAttributeInfo,
  IAttributeInfo,
  safeGetAttributeInfo,
} from '../../../../../../Model/avenyModelUtils';
import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import { IFormatter } from '../../../../../../Utils/Formatters/FormatterTypes';
import { Result } from 'true-myth';
import { asString } from '../../../../../../Utils/Formatters/Utils/ValueUtils';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';

interface IBaseProps {
  columns: IColumnDef[];
  rowNumber: number;
  object: any;
  selected: boolean;
  onClick: (rowNumber: number) => void;
}

export const findValue = (
  data: any,
  path: string[]
): string | number | null => {
  if (data == null) {
    return null;
  }
  if (path.length === 0) {
    return null;
  }
  if (path.length === 1) {
    return data[path[0]];
  }

  const associatedValue = data._Associations?.[`${path[0]}`];

  // const associatedValue = associatedData[0];
  if (associatedValue) {
    return findValue(associatedValue, tail(path));
  }
  return null;
};

interface IAttributeFormatter {
  formatter: IFormatter;
  displayParams: string;
  validateParams: string;
  parseParams: string;
}

export const getSafeAttributeFormatter = (
  path: string,
  maybeCollection: string | IClientBusinessModel,
  collectionMap: AvenyCollectionMap
): Result<IAttributeFormatter, string> => {
  try {
    const attr = getAttributeInfo(
      maybeCollection,
      path,
      collectionMap
    ).attribute;
    return Result.ok({
      formatter: findFormatter(attr.Formatter, attr.DataType),
      displayParams: attr.FormatterDisplayParams ?? '',
      validateParams: attr.FormatterValidateParams ?? '',
      parseParams: attr.FormatterParseParams ?? '',
    });
  } catch (error) {
    return Result.err('Error - cannot find attribute formatter');
  }
};

export const getAttributeFormatter = (
  path: string,
  maybeCollection: string | IClientBusinessModel,
  collectionMap: AvenyCollectionMap
): IAttributeFormatter => {
  const attr = getAttributeInfo(maybeCollection, path, collectionMap).attribute;

  return {
    formatter: findFormatter(attr.Formatter, attr.DataType),
    displayParams: attr.FormatterDisplayParams ?? '',
    validateParams: attr.FormatterValidateParams ?? '',
    parseParams: attr.FormatterParseParams ?? '',
  };
};

export const getFormattedColumnValue = (
  avenyCollectionMap: AvenyCollectionMap,
  item: ICollectionDataType | null | undefined,
  col: IColumnDef,
  collection: IClientBusinessModel
): string | null => {
  const attrInfo: Result<IAttributeInfo, string> = safeGetAttributeInfo(
    collection,
    col.attributeDef,
    avenyCollectionMap
  );

  const attributeFormatter = getSafeAttributeFormatter(
    col.attributeDef,
    collection,
    avenyCollectionMap
  );
  const attr = attrInfo.map(i => i.attribute);

  const displayer =
    attributeFormatter.isOk() && attributeFormatter.value.formatter.displayer;

  const value = findValue(item, col.attributeDef.split('.')) || '-';

  const formattedValue =
    displayer && attr.isOk()
      ? displayer.display(value, true, attr.value.FormatterDisplayParams)
      : asString(value);

  return formattedValue;
};

/**
Tabellrad för ObjectTable
*/
const ObjectTableRow: React.FunctionComponent<
  React.PropsWithChildren<IBaseProps>
> = props => {
  const handleClick = () => {
    props.onClick(props.rowNumber);
  };
  const columns = props.columns.map(column => {
    const attrPath = column.attributeDef.split('.');
    const value = findValue(props.object, attrPath);

    const cellTitle = column.cellTitleDef
      ? findValue(props.object, column.cellTitleDef.split('.'))
      : null;

    if (cellTitle) {
      return (
        <EDTableCell key={column.attributeDef}>
          <EDTypography bold>{cellTitle}</EDTypography>
          {value || ''}
        </EDTableCell>
      );
    } else {
      return <EDTableCell key={column.attributeDef}>{value || ''}</EDTableCell>;
    }
  });
  return (
    <EDTableRow
      key={props.object.id}
      selected={props.selected}
      onClick={handleClick}
    >
      {columns}
    </EDTableRow>
  );
};

/** @component */
export default ObjectTableRow;
